import type {Blame, BlameCommit, BlameRange} from '@github-ui/code-view-types'
import {useCurrentRepository} from '@github-ui/current-repository'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {blamePath, commitHovercardPath} from '@github-ui/paths'
import {Link} from '@github-ui/react-core/link'
import {SafeHTMLText} from '@github-ui/safe-html'
import {ScreenSize} from '@github-ui/screen-size'
import {useVirtualWindow} from '@github-ui/use-virtual'
import {VersionsIcon} from '@primer/octicons-react'
import {Box, IconButton, RelativeTime, Tooltip} from '@primer/react'
import React, {useCallback, useImperativeHandle, useRef} from 'react'

import {useBlameAgeColor} from '../../../../../react-shared/Repos/blameUtils'
import {useCurrentBlame} from '../../../../hooks/CurrentBlame'
import {isLineInViewport} from '../../../../utilities/lines'
import type {CodeLinesHandle} from './code-lines-handle'
import {CodeLine} from './CodeLine'
import type {CodeLineData} from './hooks/use-code-lines'
import {LineNumber} from './LineNumber'

const codeRowHeightInPixels = 20
const singleBlameLineHeightWide = 31 // 20px line height + 10px vertical padding + 1px border
const singleBlameLineHeightNarrow = 41 // 20px line height + 20px vertical padding + 1px border

interface BlameLinesProps {
  linesData: readonly CodeLineData[]
  tabSize: number
  materializeAllLines?: boolean
  copilotAccessAllowed: boolean
  onLineNumberClick?: React.MouseEventHandler<HTMLDivElement>
}
export const BlameLines = React.forwardRef(BlameLinesWithRef)

export function BlameLinesSSR({copilotAccessAllowed, linesData, tabSize, onLineNumberClick}: BlameLinesProps) {
  const parentRef = useRef<HTMLTableElement>(null)

  const blame = useCurrentBlame()
  const blameSegments = extractBlameSegments(blame, linesData)
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        tabSize,
      }}
      ref={parentRef}
      data-hpc
    >
      {blameSegments.map((segment, index) => (
        <BlameSegment
          key={`blame-for-segment-${segment.range?.start ?? segment.linesData[0]!.lineNumber}`}
          {...segment}
          index={index}
          copilotAccessAllowed={copilotAccessAllowed}
          onLineNumberClick={onLineNumberClick}
        />
      ))}
    </Box>
  )
}
function BlameLinesWithRef(
  {copilotAccessAllowed, linesData, tabSize, materializeAllLines, onLineNumberClick}: BlameLinesProps,
  ref: React.ForwardedRef<CodeLinesHandle>,
) {
  const parentRef = useRef<HTMLTableElement>(null)

  const blame = useCurrentBlame()

  useImperativeHandle(ref, () => ({
    scrollToTop: () => {
      if (isLineInViewport(0)) return
      virtualizer.scrollToIndex(0, {align: 'start'})
    },
    scrollToLine: lineNumber => {
      if (isLineInViewport(lineNumber)) return
      virtualizer.scrollToIndex(lineNumber, {align: 'center'})
    },
  }))

  /**
   * This size estimation function is used by the virtualizer to guess which elements should appear in the window.
   * It doesn't need to be perfect - it just needs to get close.
   */
  const estimateSize = useCallback(
    (index: number) => {
      const line = linesData[index]!
      const range = blame?.ranges[line.lineNumber]
      if (range && range.end === range.start) {
        //using window.innerWidth means this estimate won't be udpated if a user resizes their window. It's just
        //a guess though, so it probably will be fine?
        return window.innerWidth > ScreenSize.medium ? singleBlameLineHeightWide : singleBlameLineHeightNarrow
      }

      return codeRowHeightInPixels
    },
    [linesData, blame],
  )

  const virtualizer = useVirtualWindow({
    parentRef,
    size: linesData.length,
    overscan: materializeAllLines ? Number.MAX_SAFE_INTEGER : 100,
    estimateSize,
  })

  const virtualLinesData = virtualizer.virtualItems.map(virtualItem => ({
    ...linesData[virtualItem.index]!,
    virtualOffset: virtualItem.start,
  }))

  const blameSegments = extractBlameSegments(blame, virtualLinesData)

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: [
          // If the screen size is < 768px, we show the blame lines as a column. This means the height calculation
          // Will be wrong
          `${virtualizer.totalSize + blameSegments.length * singleBlameLineHeightNarrow}px`,
          `${virtualizer.totalSize + blameSegments.length * singleBlameLineHeightNarrow}px`,
          `${virtualizer.totalSize}px`,
        ],
        tabSize,
      }}
      ref={parentRef}
      data-hpc
    >
      {blameSegments.map((segment, index) => (
        <BlameSegment
          key={`blame-for-segment-${segment.range?.start ?? segment.linesData[0]!.lineNumber}`}
          {...segment}
          index={index}
          copilotAccessAllowed={copilotAccessAllowed}
          onLineNumberClick={onLineNumberClick}
        />
      ))}
    </Box>
  )
}

interface BlameSegmentProps extends BlameSegmentData {
  copilotAccessAllowed: boolean
  onLineNumberClick?: React.MouseEventHandler<HTMLDivElement>
}

function BlameSegment({range, commit, linesData, index, copilotAccessAllowed, onLineNumberClick}: BlameSegmentProps) {
  const virtualOffset = linesData[0]!.virtualOffset

  function BlameSegmentContent(narrowScreen: boolean) {
    return (
      <>
        {range && commit ? <BlameForRange range={range} commit={commit} /> : <Box sx={{height: '100%'}} />}
        <Box sx={{display: 'flex', flexDirection: 'row'}}>
          <Box className="react-line-numbers" sx={linesData.length > 1 ? undefined : {py: ['10px', '10px', '5px']}}>
            {linesData.map(lineData => (
              <LineNumber
                key={`line-number-${lineData.lineNumber}`}
                codeLineData={lineData}
                onClick={onLineNumberClick}
              />
            ))}
          </Box>
          <Box className="react-code-lines" sx={linesData.length > 1 ? undefined : {py: ['10px', '10px', '5px']}}>
            {linesData.map(lineData => (
              <CodeLine
                key={`code-line=${lineData.lineNumber}`}
                id={`LC${lineData.lineNumber}-${narrowScreen ? 'narrow' : 'wide'}`}
                codeLineData={lineData}
                copilotAccessAllowed={copilotAccessAllowed}
              />
            ))}
          </Box>
        </Box>
      </>
    )
  }

  return (
    <>
      <Box
        sx={{
          display: ['none', 'none', 'flex'],
          borderBottomColor: 'border.muted',
          borderBottomWidth: '1px',
          borderBottomStyle: 'solid',
          width: '100%',
          flexDirection: 'row',
        }}
        style={
          virtualOffset !== undefined
            ? {transform: `translateY(${virtualOffset}px)`, position: 'absolute', top: 0}
            : undefined
        }
      >
        {BlameSegmentContent(false)}
      </Box>
      <Box
        sx={{
          display: ['flex', 'flex', 'none'],
          borderBottomColor: 'border.muted',
          borderBottomWidth: '1px',
          borderBottomStyle: 'solid',
          width: '100%',
          flexDirection: 'column',
        }}
        style={
          virtualOffset !== undefined
            ? {
                transform: `translateY(${virtualOffset + (index ?? 0) * singleBlameLineHeightNarrow}px)`,
                position: 'absolute',
                top: 0,
              }
            : undefined
        }
      >
        {BlameSegmentContent(true)}
      </Box>
    </>
  )
}

interface CodeLineDataWithOffset extends CodeLineData {
  virtualOffset?: number
  measureRef?: ((el: HTMLElement | null) => void) | undefined
  virtualKey?: number | string
}

interface BlameSegmentData {
  commit?: BlameCommit
  range?: BlameRange
  linesData: readonly CodeLineDataWithOffset[]
  virtualOffset?: number
  index?: number
}

interface InProgressBlameSegmentData extends BlameSegmentData {
  linesData: CodeLineDataWithOffset[]
}

function extractBlameSegments(
  blame: Blame | undefined,
  linesData: readonly CodeLineDataWithOffset[],
): BlameSegmentData[] {
  if (!blame) return [{linesData}]

  const segments: InProgressBlameSegmentData[] = []

  let current: InProgressBlameSegmentData | null = null
  let isFirst = true
  for (const lineData of linesData) {
    if (!current) {
      current = {linesData: []}
    }

    const range = isFirst
      ? Object.values(blame?.ranges ?? {}).find(r => r.start <= lineData.lineNumber && r.end >= lineData.lineNumber)
      : blame?.ranges[lineData.lineNumber]
    if (range) {
      current.range = range
      current.commit = blame.commits[range.commitOid]
    }

    current.linesData.push(lineData)

    if (current.range?.end === lineData.lineNumber) {
      segments.push(current)
      current = null
    }

    isFirst = false
  }

  if (current) {
    segments.push(current)
    current = null
  }

  return segments
}

const BlameForRange = React.memo(BlameForRangeUnmemoized)

function BlameForRangeUnmemoized({range, commit}: {range: BlameRange; commit: BlameCommit}) {
  const repo = useCurrentRepository()
  const commitDate = new Date(commit.committedDate)
  const repoCreationDate = new Date(repo.createdAt)

  const timestampAgoElement = (
    <Box
      sx={{
        verticalAlign: 'top',
        pl: '10px',
        width: 100,
      }}
    >
      <RelativeTime
        date={commitDate}
        tense="past"
        sx={{color: 'fg.muted', whiteSpace: 'nowrap', fontSize: 'smaller'}}
      />
    </Box>
  )

  return (
    <Box
      className="react-blame-for-range"
      sx={{display: 'flex', minWidth: ['auto', 'auto', 350], maxWidth: ['auto', 'auto', 350]}}
    >
      <Box aria-hidden sx={{padding: '2px', width: 4}}>
        <BlameAgeIndicator commitDate={commitDate} repoCreationDate={repoCreationDate} />
      </Box>
      <Box sx={{display: ['none', 'none', 'inherit'], pt: '4px'}}>{timestampAgoElement}</Box>
      <Box
        sx={{
          pl: 1,
          pt: ['6px', '6px', '3px'],
          verticalAlign: 'top',
          width: 25,
        }}
      >
        {commit.authorAvatarUrl && <GitHubAvatar src={commit.authorAvatarUrl} size={18} />}
      </Box>
      <Box
        sx={{
          verticalAlign: 'top',
          pt: [2, 2, '6px'],
          pb: [2, 2, 0],
          // Screens < 768px, let the commit message grow to fill the space
          minWidth: [0, 0, 170],
          flexGrow: [1, 1, 1],
        }}
      >
        <Box sx={{display: 'flex'}}>
          <SafeHTMLText
            html={commit.shortMessageHtmlLink}
            sx={{
              whiteSpace: 'nowrap',
              ml: 2,
              overflowX: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
              fontSize: [1, 1, 0],
            }}
            data-hovercard-url={commitHovercardPath({owner: repo.ownerLogin, repo: repo.name, commitish: commit.oid})}
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignContent: 'flex-start',
          justifySelf: 'flex-end',
          verticalAlign: 'top',
          pl: 2,
          pt: ['2px', '2px', '1px'],
          pb: [1, 1, 0],
          width: [150, 150, 34],
          pr: [2, 2, 0],
        }}
      >
        <Box sx={{display: ['flex', 'flex', 'none'], pt: '1px', pr: 3}}>{timestampAgoElement}</Box>
        <ReblameButton range={range} commit={commit} />
      </Box>
    </Box>
  )
}

function ReblameButton({range, commit}: {range: BlameRange; commit: BlameCommit}) {
  const repo = useCurrentRepository()
  if (!range.reblamePath) return null

  const href = blamePath({
    owner: repo.ownerLogin,
    repo: repo.name,
    commitish: commit.firstParentOid,
    filePath: range.reblamePath,
  })

  const longDateFormatter = new Intl.DateTimeFormat(undefined, {year: 'numeric', month: 'short', day: 'numeric'})
  const shortSHA = commit.oid.slice(0, 7)
  const formattedDate = longDateFormatter.format(new Date(commit.committedDate))
  const blameLabel = `Blame prior to change ${shortSHA}, made on ${formattedDate}`

  return (
    <Tooltip aria-label={blameLabel}>
      <IconButton
        aria-label={blameLabel}
        as={Link}
        sx={{color: 'fg.muted'}}
        to={href}
        icon={VersionsIcon}
        variant="invisible"
        size="small"
      />
    </Tooltip>
  )
}

function BlameAgeIndicator({commitDate, repoCreationDate}: {commitDate: Date; repoCreationDate: Date}) {
  const color = useBlameAgeColor(commitDate, repoCreationDate)
  return (
    <Box
      sx={{
        backgroundColor: color,
        width: '4px',
        borderRadius: '2px',
        height: '100%',
      }}
    />
  )
}

try{ BlameLines.displayName ||= 'BlameLines' } catch {}
try{ BlameLinesSSR.displayName ||= 'BlameLinesSSR' } catch {}
try{ BlameLinesWithRef.displayName ||= 'BlameLinesWithRef' } catch {}
try{ BlameSegment.displayName ||= 'BlameSegment' } catch {}
try{ BlameSegmentContent.displayName ||= 'BlameSegmentContent' } catch {}
try{ BlameForRange.displayName ||= 'BlameForRange' } catch {}
try{ BlameForRangeUnmemoized.displayName ||= 'BlameForRangeUnmemoized' } catch {}
try{ ReblameButton.displayName ||= 'ReblameButton' } catch {}
try{ BlameAgeIndicator.displayName ||= 'BlameAgeIndicator' } catch {}